import AF from "../utlis/AF";

const CLASS_ACTIVE = "is-active";

export default class Nav {
  constructor() {
    this.el = document.querySelector("[nav]");

    this.af = AF.instance();
    this.activate = this.activate.bind(this);
    this.init();
  }

  init() {
    this.searchClose = document.querySelector(".menu-search__close");
    this.searchContainer = document.querySelector(".nav__search-container");
    this.searchBtn = document.querySelector(".nav__item--search");
    this.searchBtnMobile = document.querySelector(".nav__burger_search");
    this.searchInput = this.el.querySelector("input");

    // Initially hide search container from tab order
    if (this.searchContainer) {
      this.searchContainer.setAttribute("aria-hidden", "true");
      this.searchContainer.style.visibility = "hidden";
    }

    // Handle search button click and keyboard events
    const openSearch = () => {
      this.searchContainer.classList.add("open");
      this.searchContainer.setAttribute("aria-hidden", "false");
      this.searchContainer.style.visibility = "visible";
      this.searchBtn.setAttribute("aria-expanded", "true");
      if (this.searchBtnMobile) {
        this.searchBtnMobile.setAttribute("aria-expanded", "true");
      }
      // Make search elements tabbable when open
      this.searchInput.setAttribute("tabindex", "0");
      this.searchClose.setAttribute("tabindex", "0");
      this.searchInput.focus();
    };

    const closeSearch = (returnFocus = true) => {
      this.searchContainer.classList.remove("open");
      this.searchContainer.setAttribute("aria-hidden", "true");
      // Use setTimeout to ensure visibility is hidden after transition completes
      setTimeout(() => {
        if (!this.searchContainer.classList.contains("open")) {
          this.searchContainer.style.visibility = "hidden";
        }
      }, 200); // Match the CSS transition duration
      this.searchBtn.setAttribute("aria-expanded", "false");
      if (this.searchBtnMobile) {
        this.searchBtnMobile.setAttribute("aria-expanded", "false");
      }
      // Make search elements non-tabbable when closed
      this.searchInput.setAttribute("tabindex", "-1");
      this.searchClose.setAttribute("tabindex", "-1");
      this.searchInput.blur();

      // Return focus to the button that opened the search
      if (returnFocus) {
        // Check which button was used to open search
        const activeElement = document.activeElement;
        if (
          activeElement === this.searchClose ||
          !activeElement.closest(".menu-search")
        ) {
          this.searchBtn.focus();
        }
      }
    };

    // Desktop search button
    this.searchBtn.addEventListener("click", openSearch);
    this.searchBtn.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openSearch();
      }
    });

    // Mobile search button
    if (this.searchBtnMobile) {
      this.searchBtnMobile.addEventListener("click", openSearch);
      this.searchBtnMobile.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openSearch();
        }
      });
    }

    // Close button
    this.searchClose.addEventListener("click", () => closeSearch(true));

    // Escape key handler for search
    document.addEventListener("keydown", (e) => {
      if (
        e.key === "Escape" &&
        this.searchContainer.classList.contains("open")
      ) {
        closeSearch(true);
      }
    });

    // Initialize mega menu keyboard accessibility
    this.initMegaMenuKeyboard();

    window.setTimeout(() => {
      this.af.onNextWrite(this.activate);
    }, 1000);
  }

  activate() {
    this.el.classList.add(CLASS_ACTIVE);
  }

  initMegaMenuKeyboard() {
    this.menuItems = this.el.querySelectorAll(".nav__link--with-child");
    this.activeMenu = null;

    if (!this.menuItems.length) return;

    this.menuItems.forEach((menuItem) => {
      const container = menuItem.parentElement;
      const submenu = container.querySelector(".nav-item__container");

      if (!submenu) return;

      // Add ARIA attributes
      menuItem.setAttribute("aria-haspopup", "true");
      menuItem.setAttribute("aria-expanded", "false");

      // Generate unique ID for submenu if it doesn't have one
      if (!submenu.id) {
        submenu.id = `submenu-${Math.random().toString(36).substring(2, 11)}`;
      }
      menuItem.setAttribute("aria-controls", submenu.id);

      // Handle keyboard events
      menuItem.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.toggleMenu(menuItem, container, submenu);
        }
      });

      // Handle clicks for touch devices
      menuItem.addEventListener("click", (e) => {
        // Only prevent default if it's a button or has no href
        const href = menuItem.getAttribute("href");
        if (menuItem.tagName === "BUTTON" || !href || href === "#") {
          e.preventDefault();
          this.toggleMenu(menuItem, container, submenu);
        }
      });

      // Close menu when pressing Escape inside submenu
      submenu.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          this.closeMenu(menuItem, container, submenu);
          menuItem.focus();
        }
      });
    });

    // Close menu when clicking outside
    document.addEventListener("click", (e) => {
      if (this.activeMenu && !this.activeMenu.container.contains(e.target)) {
        this.closeMenu(
          this.activeMenu.menuItem,
          this.activeMenu.container,
          this.activeMenu.submenu
        );
      }
    });

    // Close menu when tabbing away
    document.addEventListener("focusin", (e) => {
      if (this.activeMenu && !this.activeMenu.container.contains(e.target)) {
        this.closeMenu(
          this.activeMenu.menuItem,
          this.activeMenu.container,
          this.activeMenu.submenu
        );
      }
    });

    // Global Escape key handler for mega menu
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.activeMenu) {
        this.closeMenu(
          this.activeMenu.menuItem,
          this.activeMenu.container,
          this.activeMenu.submenu
        );
        this.activeMenu.menuItem.focus();
      }
    });
  }

  toggleMenu(menuItem, container, submenu) {
    const isOpen = menuItem.getAttribute("aria-expanded") === "true";

    if (isOpen) {
      this.closeMenu(menuItem, container, submenu);
    } else {
      // Close any other open menu
      if (this.activeMenu) {
        this.closeMenu(
          this.activeMenu.menuItem,
          this.activeMenu.container,
          this.activeMenu.submenu
        );
      }
      this.openMenu(menuItem, container, submenu);
    }
  }

  openMenu(menuItem, container, submenu) {
    menuItem.setAttribute("aria-expanded", "true");

    // Apply the same styles that CSS hover applies
    submenu.style.visibility = "visible";
    submenu.style.opacity = "1";
    submenu.style.transform = "translateX(0) translateY(-15px) scaleY(1)";

    this.activeMenu = { menuItem, container, submenu };

    // Focus first link in submenu if opened via keyboard
    if (document.activeElement === menuItem) {
      const firstLink = submenu.querySelector("a");
      if (firstLink) {
        setTimeout(() => firstLink.focus(), 100);
      }
    }
  }

  closeMenu(menuItem, container, submenu) {
    menuItem.setAttribute("aria-expanded", "false");

    // Remove inline styles to let CSS handle the hidden state
    submenu.style.visibility = "";
    submenu.style.opacity = "";
    submenu.style.transform = "";

    if (this.activeMenu && this.activeMenu.menuItem === menuItem) {
      this.activeMenu = null;
    }
  }
}
